import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Chip,
  Box,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Avatar,
  TableHead,
} from "@mui/material";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TaskCalendar from "../task-calendar";
import SelectedTaskDetailsManager from "./managerTaskDetails/selected-tast-details-manager";
import { filterTasksByCalendar } from "../../../utils/calendarFilter";
import SelectedTaskExportManager from "./managerTasksExport/selected-task-export-manager";
import Loading from "../../../common/loader/loading";

const ManagerKeyHandlingCheckView = ({ onClose, selectedMainTask }) => {
  const [tasks, setTasks] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCalendar, setOpenCalendar] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState({});

  // Handle month navigation
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleSelectedDatesUpdate = (dates) => {
    setSelectedDates(dates);
    const updatedTasks = filterTasksByCalendar(tasks, dates, "date");
    setFilteredTasks(updatedTasks);
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/signin/homeKeyAll");
      if (response.data.success) {
        const grouped = response.data.records.reduce((acc, task) => {
          const taskDate = new Date(task.date).toISOString().split("T")[0];
          if (!acc[taskDate]) acc[taskDate] = [];
          acc[taskDate].push(task);
          return acc;
        }, {});
        setFilteredTasks(grouped);
        setTasks(response.data.records);
      } else {
        console.error("Invalid response format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <Grid container spacing={2} mt={2}>
      <Grid item lg={9} md={8} xs={12}>
        <Box sx={{ p: 4, ml: 2, mr: 2 }}>
          <Typography sx={{ fontSize: "14px" }}>
            <Grid container>
              <Grid item>
                <KeyboardBackspaceIcon onClick={onClose} style={{ cursor: "pointer" }} />
              </Grid>
              <Grid item> Back to home </Grid>
            </Grid>
          </Typography>
          <Box mt={3} display='flex' justifyContent='space-between' mb={2}>
            <Box>
              <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
                Key Handling Check List
              </Typography>
            </Box>
            <Box display='flex' gap={2}>
              <Button
                variant='outlined'
                color='primary'
                onClick={() => {
                  setOpenCalendar(true);
                  setSelectedTask(null);
                }}
              >
                <CalendarMonthOutlinedIcon />
                Calendar
              </Button>
            </Box>
          </Box>

          {Object.entries(filteredTasks).map(([date, dateTasks]) => (
            <Box key={date}>
              <Paper
                elevation={3}
                sx={{
                  padding: "20px 25px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  mb: "10px",
                }}
                onClick={() => setExpandedRow(expandedRow === date ? null : date)}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: "bold", fontSize: "16px" }}>
                    Key Management Check List
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Typography variant='body2' color='black'>
                    {new Date(date).toISOString().split("T")[0]}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <IconButton sx={{ color: "black" }}>
                    {expandedRow === date ? <ExpandLessIcon /> : <ExpandCircleDownIcon />}
                  </IconButton>
                </Box>
              </Paper>

              <Collapse in={expandedRow === date} timeout='auto' unmountOnExit>
                <Box sx={{ padding: "20px", borderRadius: "5px" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: "12px" }}>Key No</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>Key Category</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>Sign In</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>Sign Out</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>Status</TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(dateTasks) && dateTasks.length > 0 ? (
                        dateTasks.map((task) => (
                          <TableRow
                            key={task.id}
                            onClick={() => {
                              setSelectedTask({
                                task,
                                taskName: "Key Management Check List",
                                mainTask: selectedMainTask,
                              }); // Set the clicked task details
                              setOpenCalendar(false); // Close the calendar view if open
                            }}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#f5f5f5" },
                            }}
                          >
                            <TableCell>{task.key_no}</TableCell>
                            <TableCell>{task.key_category}</TableCell>
                            <TableCell>{task.pickup_time}</TableCell>
                            <TableCell>{task.dropoff_time || "Not yet returned"}</TableCell>
                            <TableCell>
                              <Chip
                                variant='contained'
                                sx={{
                                  fontSize: "12px",
                                  borderRadius: 5,
                                  backgroundColor: task.dropoff_time ? "#EAFFF2" : "#FFF3E0",
                                  color: task.dropoff_time ? "green" : "orange",
                                }}
                                label={task.dropoff_time ? "Completed" : "Pending"}
                                size='small'
                              />
                            </TableCell>
                            <TableCell>
                              <Box display='flex' alignItems='center'>
                                <Avatar
                                  alt='Profile'
                                  src='/images/person.png'
                                  sx={{ width: 40, height: 40, marginRight: 2 }}
                                />
                                {task.employee_name}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <Typography>No tasks for this date.</Typography>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid item lg={3} md={4} xs={12}>
        {openCalendar && (
          <>
            <Box sx={{ p: 4, mr: 2, ml: 2 }}>
              <TaskCalendar
                currentDate={currentDate}
                selectedDates={selectedDates}
                handlePrevMonth={handlePrevMonth}
                handleNextMonth={handleNextMonth}
                handleSelectedDatesUpdate={handleSelectedDatesUpdate}
              />
            </Box>
            <Box sx={{ p: 2, mr: 2, mt: 3, ml: 2 }}>
              <SelectedTaskExportManager
                keysTasks={Object.entries(filteredTasks)}
                taskName='Key Management Check List'
              />
            </Box>
          </>
        )}
        {selectedTask && (
          <>
            <Box sx={{ p: 2, mr: 2, ml: 2 }}>
              <SelectedTaskDetailsManager selectedTask={selectedTask} />
            </Box>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default ManagerKeyHandlingCheckView;
